import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrashIcon, EyeIcon, LockClosedIcon, CalendarIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon, BanknotesIcon } from '@heroicons/react/24/solid';
import { Student } from '../types/student';
import TableRowSkeleton from '../components/TableRowSkeleton';
import type { FormEvent, ChangeEvent } from 'react';
import { apiUrl, authFetch, authService } from '../utils/api';

interface SortConfig {
  key: keyof Student | null;
  direction: 'asc' | 'desc';
}

interface Filters {
  userName: string;
  academicYear: string;
  email: string;
  phonenumber: string;
  registrationDateStart: string;
  registrationDateEnd: string;
  status: string;
}

interface SearchParams {
  name: string;
  phonenumber: string;
  email: string;
}

interface StudentFormData {
  name: string;
  email: string;
  password: string;
  grade?: string;
  mobile?: string;
  confirmPassword?: string;
}

const Students = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);

  const [filters, setFilters] = useState<Filters>({
    userName: '',
    academicYear: '',
    email: '',
    phonenumber: '',
    registrationDateStart: '',
    registrationDateEnd: '',
    status: '',
  });

  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    userName: '',
    academicYear: '',
    email: '',
    phonenumber: '',
    registrationDateStart: '',
    registrationDateEnd: '',
    status: '',
  });

  const [searchParams, setSearchParams] = useState<SearchParams>({
    name: '',
    phonenumber: '',
    email: ''
  });

  const [appliedSearchParams, setAppliedSearchParams] = useState<SearchParams>({
    name: '',
    phonenumber: '',
    email: ''
  });

  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [studentToBlock, setStudentToBlock] = useState<Student | null>(null);
  const [studentToAddCoins, setStudentToAddCoins] = useState<Student | null>(null);
  const [coinsAmount, setCoinsAmount] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isAddingCoins, setIsAddingCoins] = useState(false);
  const [showRegDatePicker, setShowRegDatePicker] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const [regDateMonth, setRegDateMonth] = useState(new Date().getMonth());
  const [regDateYear, setRegDateYear] = useState(new Date().getFullYear());

  const [formData, setFormData] = useState<StudentFormData>({
    name: '',
    email: '',
    password: '',
    grade: '',
    mobile: '',
    confirmPassword: '',
  });

  const studentData = location.state?.studentData;

  useEffect(() => {
    // Only fetch on initial load or when current page changes
    fetchStudents();
    
    // Check if we have a success message from navigation state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear after 3 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        // Clear the navigation state
        window.history.replaceState({}, document.title);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentPage, location.state?.message]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowRegDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchStudents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let url;
      const hasSearchFilters = appliedSearchParams.name || appliedSearchParams.phonenumber || appliedSearchParams.email || 
                             appliedFilters.academicYear || appliedFilters.registrationDateStart || 
                             appliedFilters.registrationDateEnd || appliedFilters.status;
      
      if (hasSearchFilters) {
        // Use search endpoint when filters are applied
        const params = new URLSearchParams();
        
        // Add search parameters if they exist
        if (appliedSearchParams.name) params.append('name', appliedSearchParams.name);
        if (appliedSearchParams.phonenumber) params.append('phoneNumber', appliedSearchParams.phonenumber);
        if (appliedSearchParams.email) params.append('email', appliedSearchParams.email);
        
        // Add filter parameters
        if (appliedFilters.academicYear) params.append('academicYear', appliedFilters.academicYear);
        if (appliedFilters.registrationDateStart) params.append('fromdate', appliedFilters.registrationDateStart);
        if (appliedFilters.registrationDateEnd) params.append('Todate', appliedFilters.registrationDateEnd);
        if (appliedFilters.status) params.append('active', (appliedFilters.status === 'active').toString());
        
        // Add pagination parameters
        params.append('pageNumber', currentPage.toString());
        params.append('pageSize', pageSize.toString());

        url = apiUrl(`AdminStudent/Studentsearch?${params.toString()}`);
      } else {
        url = apiUrl(`AdminStudent/all?pageNumber=${currentPage}&pageSize=${pageSize}`);
      }

      console.log('Fetching students from URL:', url);

      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        console.error('API response not OK:', response.status, response.statusText);
        throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('API Response:', result);
      
      if (!result.success) {
        console.error('API returned success: false:', result);
        throw new Error(result.message || 'Failed to fetch students');
      }

      // Log the structure of the response
      console.log('Response data structure:', {
        hasData: !!result.data,
        totalStudents: result.data?.totalStudents,
        totalPages: result.data?.totalPages,
        studentsLength: result.data?.students?.length
      });

      // If no data property exists at all
      if (!result.data) {
        console.warn('No data property in response');
        setStudents([]);
        setTotalPages(0);
        setTotalCount(0);
        return;
      }

      // Get the students array, defaulting to empty array if not present
      const studentsArray = result.data.students || [];
      
      // Get total counts from the response data
      const totalStudents = result.data.totalStudents;
      const totalCount = result.data.totalCount;
      const totalPages = result.data.totalPages;

      // Map the response data to include status
      const studentsWithStatus = studentsArray.map((student: any) => ({
        ...student,
        status: student.active ? 'active' : 'blocked'
      }));
      
      setStudents(studentsWithStatus);
      setTotalPages(totalPages);
      setTotalCount(totalCount || totalStudents);
      setTotalStudents(result.data.totalStudents);
      
      // Log final state updates
      console.log('Updated state:', {
        studentsCount: studentsWithStatus.length,
        totalPages,
        totalStudents
      });

    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students. Please try again later.');
      setStudents([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (key: keyof Student) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig?.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    // Create the new filter values
    const newAppliedFilters = { ...filters };
    const newAppliedSearchParams = { ...searchParams };

    // Use these values directly in the API call
    const params = new URLSearchParams();
    
    // Add search parameters if they exist
    if (searchParams.name) params.append('name', searchParams.name);
    if (searchParams.phonenumber) params.append('phoneNumber', searchParams.phonenumber);
    if (searchParams.email) params.append('email', searchParams.email);
    
    // Add filter parameters
    if (filters.academicYear) params.append('academicYear', filters.academicYear);
    if (filters.registrationDateStart) params.append('fromdate', filters.registrationDateStart);
    if (filters.registrationDateEnd) params.append('Todate', filters.registrationDateEnd);
    if (filters.status) params.append('active', (filters.status === 'active').toString());
    
    // Add pagination parameters
    params.append('pageNumber', '1');
    params.append('pageSize', pageSize.toString());

    // Update the states
    setAppliedFilters(newAppliedFilters);
    setAppliedSearchParams(newAppliedSearchParams);
    setCurrentPage(1);

    // Make the API call directly with current values
    setIsLoading(true);
    setError(null);

    const hasSearchFilters = searchParams.name || searchParams.phonenumber || searchParams.email || 
                           filters.academicYear || filters.registrationDateStart || 
                           filters.registrationDateEnd || filters.status;

    const url = hasSearchFilters 
      ? apiUrl(`AdminStudent/Studentsearch?${params.toString()}`)
      : apiUrl(`AdminStudent/all?pageNumber=1&pageSize=${pageSize}`);

    authFetch(url, {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authService.getToken()}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(result => {
        if (!result.success) {
          throw new Error(result.message || 'Failed to fetch students');
        }

        if (!result.data) {
          setStudents([]);
          setTotalPages(0);
          setTotalCount(0);
          return;
        }

        const studentsArray = result.data.students || [];
        const totalStudents = result.data.totalStudents;
        const totalCount = result.data.totalCount;
        const totalPages = result.data.totalPages;

        const studentsWithStatus = studentsArray.map((student: any) => ({
          ...student,
          status: student.active ? 'active' : 'blocked'
        }));
        
        setStudents(studentsWithStatus);
        setTotalPages(totalPages);
        setTotalCount(totalCount || totalStudents);
        setTotalStudents(result.data.totalStudents);
      })
      .catch(error => {
        console.error('Error fetching students:', error);
        setError('Failed to load students. Please try again later.');
        setStudents([]);
        setTotalPages(0);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetFilters = () => {
    setFilters({
      userName: '',
      academicYear: '',
      email: '',
      phonenumber: '',
      registrationDateStart: '',
      registrationDateEnd: '',
      status: '',
    });
    setSearchParams({
      name: '',
      phonenumber: '',
      email: ''
    });
    setAppliedFilters({
      userName: '',
      academicYear: '',
      email: '',
      phonenumber: '',
      registrationDateStart: '',
      registrationDateEnd: '',
      status: '',
    });
    setAppliedSearchParams({
      name: '',
      phonenumber: '',
      email: ''
    });
    setCurrentPage(1);
    fetchStudents();
  };

  // Remove client-side filtering since it's handled by the server
  const filteredStudents = students;

  // Remove client-side pagination calculation since it's handled by the server
  const currentStudents = students;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteStudent = async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl(`AdminStudent/delete/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setStudents(prev => prev.filter(student => student.id !== id));
        setStudentToDelete(null);
        setSuccessMessage('Student deleted successfully');
        // Clear after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(data.message || 'Failed to delete student');
      }
    } catch (error) {
      setError('Failed to delete student. Please try again later.');
    } finally {
      setIsDeleting(false);
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (student: Student) => {
    setIsBlocking(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/updatestatus/${student.id}?Studentid=${student.id}`),
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        // Update the local state with the new status
        setStudents(prev => 
          prev.map(s => 
            s.id === student.id 
                ? { ...s, status: s.status === 'active' ? 'blocked' : 'active', active: s.status === 'blocked' } 
              : s
          )
        );
        setStudentToBlock(null);
        setSuccessMessage(result.data || 'Student status updated successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to update student status');
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      setError('Failed to update student status. Please try again later.');
    } finally {
      setIsBlocking(false);
    }
  };

  const handleAddCoins = async () => {
    if (!studentToAddCoins) return;
    
    const coins = parseInt(coinsAmount);
    if (isNaN(coins) || coins <= 0) {
      setError('Please enter a valid number of coins');
      return;
    }

    setIsAddingCoins(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl('Coins/grant'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({ 
            studentId: studentToAddCoins.id,
            amount: coins 
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setStudentToAddCoins(null);
        setCoinsAmount('');
        setSuccessMessage(`Successfully added ${coins} coins to ${studentToAddCoins.userName}`);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to add coins');
      }
    } catch (error) {
      console.error('Error adding coins:', error);
      setError('Failed to add coins. Please try again later.');
    } finally {
      setIsAddingCoins(false);
    }
  };

  // Calendar helper functions
  const getMonthName = (month: number, year: number) => {
    return new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (date: string) => {
    const selectedDate = new Date(date);
    const formattedDate = selectedDate.toISOString().split('T')[0];

    if (!filters.registrationDateStart || filters.registrationDateEnd) {
      // Start a new range
      setFilters({
        ...filters,
        registrationDateStart: formattedDate,
        registrationDateEnd: ''
      });
    } else {
      // Complete the range
      const startDate = new Date(filters.registrationDateStart);
      const endDate = new Date(formattedDate);
      
      if (endDate < startDate) {
        // If end date is before start date, swap them
        setFilters({
          ...filters,
          registrationDateStart: formattedDate,
          registrationDateEnd: filters.registrationDateStart
        });
      } else {
        setFilters({
          ...filters,
          registrationDateEnd: formattedDate
        });
      }
      setShowRegDatePicker(false);
    }
  };

  const isDateSelected = (dateString: string) => {
    return dateString === filters.registrationDateStart || dateString === filters.registrationDateEnd;
  };

  const isDateInRange = (dateString: string) => {
    if (!filters.registrationDateStart || !filters.registrationDateEnd) return false;
    
    const date = new Date(dateString);
    const start = new Date(filters.registrationDateStart);
    const end = new Date(filters.registrationDateEnd);
    
    return date >= start && date <= end;
  };

  // Calendar rendering section
  const renderCalendar = () => {
    const days = Array.from({ length: getDaysInMonth(regDateMonth, regDateYear) }, (_, i) => {
      const date = new Date(regDateYear, regDateMonth, i + 1);
      const dateString = date.toISOString().split('T')[0];
      const isSelected = isDateSelected(dateString);
      const isInRange = isDateInRange(dateString);
      
      return (
        <button
          key={i}
          onClick={() => handleDateSelect(dateString)}
          className={`
            relative h-10 w-10 flex items-center justify-center text-sm
            ${isSelected 
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 font-semibold z-10' 
              : isInRange
                ? 'bg-indigo-50 text-indigo-600'
                : 'text-gray-900 hover:bg-gray-100'
            }
            ${dateString === filters.registrationDateStart ? 'rounded-l-full' : ''}
            ${dateString === filters.registrationDateEnd ? 'rounded-r-full' : ''}
            ${!isSelected && !isInRange ? 'rounded-full' : ''}
          `}
        >
          {i + 1}
        </button>
      );
    });

    const emptyCells = Array.from({ length: getFirstDayOfMonth(regDateMonth, regDateYear) }, (_, i) => (
      <div key={`empty-${i}`} className="h-10 w-10" />
    ));

    return [...emptyCells, ...days];
  };

  // Date picker dropdown content
  const renderDatePicker = () => (
    <div className="absolute z-10 mt-1 w-[320px] bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            type="button"
            onClick={handleRegDatePrevMonth}
            className="p-1 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-gray-900">
            {getMonthName(regDateMonth, regDateYear)}
          </span>
          <button 
            type="button"
            onClick={handleRegDateNextMonth}
            className="p-1 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="mb-2 grid grid-cols-7 gap-0">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="h-10 w-10 flex items-center justify-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
          {renderCalendar()}
        </div>

        {/* Selected Range Display */}
        {(filters.registrationDateStart || filters.registrationDateEnd) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {filters.registrationDateStart && (
                  <span>
                    From: {new Date(filters.registrationDateStart).toLocaleDateString()}
                  </span>
                )}
                {filters.registrationDateEnd && (
                  <span className="ml-2">
                    To: {new Date(filters.registrationDateEnd).toLocaleDateString()}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    ...filters,
                    registrationDateStart: '',
                    registrationDateEnd: ''
                  });
                }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const formatDateDisplay = (startDate: string, endDate: string) => {
    if (!startDate && !endDate) return '';
    
    const formatDate = (date: string) => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (startDate && !endDate) return formatDate(startDate);
    if (!startDate && endDate) return `until ${formatDate(endDate)}`;
    return `${formatDate(startDate)} to ${formatDate(endDate)}`;
  };

  const handleRegDatePrevMonth = () => {
    if (regDateMonth === 0) {
      setRegDateMonth(11);
      setRegDateYear(regDateYear - 1);
    } else {
      setRegDateMonth(regDateMonth - 1);
    }
  };

  const handleRegDateNextMonth = () => {
    if (regDateMonth === 11) {
      setRegDateMonth(0);
      setRegDateYear(regDateYear + 1);
    } else {
      setRegDateMonth(regDateMonth + 1);
    }
  };

  const validateMobile = (mobile: string): boolean => {
    // Phone number should be 11 digits and start with 01
    const mobileRegex = /^01[0-9]{9}$/;
    return mobileRegex.test(mobile);
  };
  
  const validatePassword = (password: string): boolean => {
    // Password should be at least 6 characters long
    return password.length >= 6;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);

      // Construct the request body with student data
      const requestBody = {
        name: formData.name,
        email: formData.email,
        password: formData.password
      };

      // Make the API call
      const response = await authFetch(apiUrl('students'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          const errorMessages = Object.values(result.errors)
            .flat()
            .join(', ');
          throw new Error(errorMessages);
        }
        throw new Error(result.message || 'Failed to add student');
      }

      if (result.success) {
        setSuccessMessage('Student added successfully');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
        
        // Refresh the students list
        await fetchStudents();
        
        // Close the add form
        setShowAddForm(false);
      } else {
        throw new Error(result.message || 'Failed to add student');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }

    // Reset form
    setFormData({
      name: '',
      email: '',
      password: '',
      grade: '',
      mobile: '',
      confirmPassword: ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update this function to navigate directly without making an unnecessary API call
  const handleViewStudentDetails = (studentId: string) => {
    // Navigate directly to student details page without making a validation API call
    navigate(`/students/${studentId}`);
  };

  // Apply sorting to the current page of data
  const sortedStudents = useMemo(() => {
    if (!sortConfig || !sortConfig.key) return students;
    
    return [...students].sort((a, b) => {
      const key = sortConfig.key as keyof Student;
      const direction = sortConfig.direction;
      
      const aValue = a[key];
      const bValue = b[key];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) {
        return direction === 'asc' ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return direction === 'asc' ? -1 : 1;
      }
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [students, sortConfig]);

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Main Header */}
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-dark">الطلاب</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="mr-3">
                <h3 className="text-sm font-medium text-red-800">خطأ</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className="fixed top-4 left-4 z-50 animate-fade-in-down">
            <div className="rounded-lg bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="mr-3">
                  <p className="text-sm font-medium text-gray-900">{successMessage}</p>
                </div>
                <div className="mr-4 flex-shrink-0">
                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="inline-flex text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
                البحث والتصفية
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">اسم الطالب</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={searchParams.name}
                    onChange={handleSearchChange}
                    placeholder="البحث بالاسم"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">البريد الإلكتروني</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={searchParams.email}
                    onChange={handleSearchChange}
                    placeholder="البحث بالبريد الإلكتروني"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">رقم الهاتف</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="phonenumber"
                    value={searchParams.phonenumber}
                    onChange={handleSearchChange}
                    placeholder="البحث برقم الهاتف"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">السنة الدراسية</label>
                <select
                  name="academicYear"
                  value={filters.academicYear}
                  onChange={handleFilterChange}
                  className="block w-full pr-3 pl-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  lang="ar"
                >
                  <option value="">جميع السنوات</option>
                  <option value="secondary1">الصف الأول الثانوي</option>
                  <option value="secondary2">الصف الثاني الثانوي</option>
                  <option value="secondary3">الصف الثالث الثانوي</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">الحالة</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="block w-full pr-3 pl-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  lang="ar"
                >
                  <option value="">جميع الحالات</option>
                  <option value="active">نشط</option>
                  <option value="blocked">محظور</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">تاريخ التسجيل</label>
                <div className="relative" ref={datePickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowRegDatePicker(!showRegDatePicker)}
                    className="w-full bg-white px-3 py-2 border border-gray-300 rounded-md shadow-sm text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className={filters.registrationDateStart || filters.registrationDateEnd ? 'text-gray-900' : 'text-gray-400'}>
                        {formatDateDisplay(filters.registrationDateStart, filters.registrationDateEnd) || 'Select date range'}
                      </span>
                    </div>
                  </button>
                  
                  {/* Date Picker Dropdown */}
                  {showRegDatePicker && renderDatePicker()}
                </div>
              </div>
            </div>
            
            {/* Filter Action Buttons - Moved to bottom */}
            <div className="flex items-center justify-start space-x-reverse space-x-3 mt-6">
              <button
                onClick={handleResetFilters}
                className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 mr-3"
              >
               إعادة تعيين
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-3 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors duration-200"
              >
                تطبيق التصفية
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              العدد الكلي: {totalCount}
              {totalStudents && totalStudents !== totalCount && (
                <span className="text-sm text-gray-500 mr-2">(إجمالي الطلاب: {totalStudents})</span>
              )}
            </span>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              إضافة طالب جديد
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                {[
                  { key: 'userName', label: 'الاسم' },
                  { key: 'academicYear', label: 'السنة الدراسية' },
                  { key: 'email', label: 'البريد الإلكتروني' },
                  { key: 'phonenumber', label: 'رقم الهاتف' },
                  { key: 'registrationDate', label: 'تاريخ التسجيل' },
                  { key: 'isOnline', label: 'سنتر/اونلاين' },
                  { key: 'coins', label: 'المحفظة' },
                  { key: 'status', label: 'الحالة' },
                  { key: 'actions', label: 'الإجراءات' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    scope="col" 
                    className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      key === 'actions' ? 'text-left' : ''
                    }`}
                  >
                    {key !== 'actions' ? (
                      <button
                        className="group inline-flex items-center space-x-reverse space-x-1 hover:text-gray-900"
                        onClick={() => key !== 'actions' && handleSort(key as keyof Student)}
                      >
                        <span>{label}</span>
                        {sortConfig?.key === key && (
                          <svg 
                            className={`h-4 w-4 transition-transform ${
                              sortConfig.direction === 'asc' ? 'rotate-0' : 'rotate-180'
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    ) : label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRowSkeleton key={index} />
                ))
              ) : students.length > 0 ? (
                students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewStudentDetails(student.id)}
                        className="text-indigo-600 hover:text-indigo-900 font-medium"
                        dir="auto"
                      >
                        {student.userName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" dir="auto">
                        {student.academicYear}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate max-w-[200px]" dir="ltr">
                        {student.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.phonenumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(student.registrationDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.isOnline === true
                          ? 'bg-green-100 text-green-800' 
                          : student.isOnline === false
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {student.isOnline === true ? 'أونلاين' : student.isOnline === false ? 'سنتر' : 'غير محدد'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.coins ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`h-1.5 w-1.5 ml-1.5 rounded-full ${
                          student.status === 'active' ? 'bg-green-400' : 'bg-red-400'
                        }`}></span>
                        {student.status === 'active' ? 'نشط' : 'محظور'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-reverse space-x-3">
                        <button
                          onClick={() => setStudentToAddCoins(student)}
                          className="group relative text-green-500 hover:text-green-700"
                        >
                          <BanknotesIcon className="w-5 h-5" />
                          <span className="sr-only">إضافة عملات</span>
                          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-24 px-2 py-1 text-xs text-center text-white bg-gray-900 rounded-md">
                            إضافة عملات
                          </span>
                        </button>
                        <button
                          onClick={() => setStudentToBlock(student)}
                          className={`group relative ${
                            student.status === 'active' 
                              ? 'text-yellow-500 hover:text-yellow-700'
                              : 'text-green-500 hover:text-green-700'
                          }`}
                        >
                          <LockClosedIcon className="w-5 h-5" />
                          <span className="sr-only">
                            {student.status === 'active' ? 'حظر' : 'تنشيط'} الطالب
                          </span>
                          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-24 px-2 py-1 text-xs text-center text-white bg-gray-900 rounded-md">
                            {student.status === 'active' ? 'حظر' : 'تنشيط'} الطالب
                          </span>
                        </button>
                        <button
                          onClick={() => setStudentToDelete(student)}
                          className="group relative text-red-500 hover:text-red-700"
                          disabled={isLoading}
                        >
                          <TrashIcon className="w-5 h-5" />
                          <span className="sr-only">حذف الطالب</span>
                          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-24 px-2 py-1 text-xs text-center text-white bg-gray-900 rounded-md">
                            حذف الطالب
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">لم يتم العثور على طلاب</h3>
                      <p className="mt-1 text-sm text-gray-500">حاول تعديل البحث أو التصفية للعثور على ما تبحث عنه.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
                عرض <span className="font-medium">{totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> إلى{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalCount || 0)}</span>{' '}
                من <span className="font-medium">{(totalCount || 0).toLocaleString()}</span> نتيجة
              </p>
            </div>
            <div className="flex justify-center sm:justify-end space-x-reverse space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">الأول</span>
                <span>&laquo;</span>
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">السابق</span>
                <span>&lsaquo;</span>
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => 
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage !== page - 1) {
                    return [
                      <span
                        key={`ellipsis-${prevPage}`}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>,
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                }).flat()}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">التالي</span>
                <span>&rsaquo;</span>
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">الأخير</span>
                <span>&raquo;</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      {showAddForm && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto relative z-10">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة طالب جديد</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الاسم الكامل
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل اسم الطالب الكامل"
                      lang="ar"
                      dir="auto"
                    />
                  </div>

                  {/* Academic Year */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      السنة الدراسية
                    </label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">اختر السنة الدراسية</option>
                      <option value="secondary1">الصف الأول الثانوي</option>
                      <option value="secondary2">الصف الثاني الثانوي</option>
                      <option value="secondary3">الصف الثالث الثانوي</option>
                    </select>
                  </div>

                  {/* Email Address */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="student@example.com"
                      lang="ar"
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل رقم الهاتف"
                      lang="ar"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      يجب أن يكون 11 رقمًا يبدأ بـ '01'
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      كلمة المرور
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      minLength={6}
                      placeholder="••••••"
                      lang="ar"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      الحد الأدنى 6 أحرف
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      تأكيد كلمة المرور
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      minLength={6}
                      placeholder="••••••"
                      lang="ar"
                    />
                  </div>
                </div>

                <div className="flex justify-start gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    disabled={isLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        جاري الإضافة...
                      </span>
                    ) : 'إضافة طالب'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {studentToDelete && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setStudentToDelete(null)}>
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    حذف الطالب
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      هل أنت متأكد من حذف {studentToDelete.userName}؟ لا يمكن التراجع عن هذا الإجراء.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleDeleteStudent(studentToDelete.id)}
                  disabled={isDeleting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </span>
                  ) : 'حذف'}
                </button>
                <button
                  type="button"
                  onClick={() => setStudentToDelete(null)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Student Modal */}
      {studentToBlock && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setStudentToBlock(null)}>
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <LockClosedIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {studentToBlock.status === 'active' ? 'حظر الطالب' : 'تنشيط الطالب'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      هل أنت متأكد من {studentToBlock.status === 'active' ? 'حظر' : 'تنشيط'} {studentToBlock.userName}؟ 
                      {studentToBlock.status === 'active' 
                        ? ' لن يتمكن من الوصول إلى حسابه حتى يتم إعادة تنشيطه.'
                        : ' سيتمكن من الوصول إلى حسابه مرة أخرى.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    handleToggleStatus(studentToBlock);
                  }}
                  disabled={isBlocking}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    studentToBlock.status === 'active'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    studentToBlock.status === 'active'
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  } disabled:opacity-50`}
                >
                  {isBlocking ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري المعالجة...
                    </span>
                  ) : studentToBlock.status === 'active' ? 'حظر' : 'تنشيط'}
                </button>
                <button
                  type="button"
                  onClick={() => setStudentToBlock(null)}
                  disabled={isBlocking}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Coins Modal */}
      {studentToAddCoins && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => {
              setStudentToAddCoins(null);
              setCoinsAmount('');
            }}>
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <BanknotesIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    إضافة عملات للطالب
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      إضافة عملات لـ {studentToAddCoins.userName}
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">عدد العملات</label>
                      <input
                        type="number"
                        min="1"
                        value={coinsAmount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCoinsAmount(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="أدخل عدد العملات"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row">
                <button
                  type="button"
                  onClick={handleAddCoins}
                  disabled={isAddingCoins || !coinsAmount || parseInt(coinsAmount) <= 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingCoins ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الإضافة...
                    </span>
                  ) : 'إضافة'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStudentToAddCoins(null);
                    setCoinsAmount('');
                  }}
                  disabled={isAddingCoins}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;